/* global process */
import { createPdfDocumentCache } from "./pdfDocumentCache.mjs";
import { readPdfPageRotation } from "./pdfOrientation.mjs";

let enginePromise;
let scheduled = false;
const documents = createPdfDocumentCache();

export function clearPdfDocuments() { documents.clear(); }

// Share code and the worker. Small parsed PDFs use the byte-validated cache below.
export function loadPdfEngine() {
    if (!enginePromise) {
        enginePromise = import(/* webpackChunkName: "work-pdf-engine" */ "pdfjs-dist/legacy/build/pdf.mjs").then(async (api) => {
            const worker = new api.PDFWorker({ port: new Worker(new URL("./pdfWorker.js", import.meta.url), { type: "module", name: "pdf-worker" }) });
            try {
                const [viewer] = await Promise.all([
                    import(/* webpackChunkName: "work-pdf-pages" */ "pdfjs-dist/legacy/web/pdf_viewer.mjs"),
                    worker.promise,
                ]);
                return { api, viewer, worker };
            } catch (error) {
                worker.destroy();
                throw error;
            }
        }).catch((error) => { enginePromise = undefined; throw error; });
    }
    return enginePromise;
}

export function warmPdfEngine() {
    if (scheduled) return;
    scheduled = true;
    const warm = () => loadPdfEngine().catch(() => { scheduled = false; });
    if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 2000 });
    else window.setTimeout(warm, 300);
}

export async function preparePdfDocument(url, engineReady = loadPdfEngine(), { signal, prefetchOnly = false, verifiedAlternate } = {}) {
    const { api, worker } = await engineReady;
    const base = `${process.env.BASE_URL}pdf-engine/`;
    // Small drawings avoid the worker ↔ network streaming handshake. Large PDFs
    // retain PDF.js range loading instead of waiting for the entire document.
    const response = await fetch(url, { signal, cache: "no-cache" });
    if (!response.ok) throw new Error("Il PDF non è disponibile. Riprova o verifica il documento.");
    const length = Number(response.headers.get("content-length"));
    if (prefetchOnly && !(length > 0 && length <= 8 * 1024 * 1024)) { await response.body?.cancel(); return null; }
    let source, hash;
    if (length > 0 && length <= 8 * 1024 * 1024) {
        const bytes = await response.arrayBuffer();
        if (window.crypto?.subtle) {
            const digest = await window.crypto.subtle.digest("SHA-256", bytes);
            hash = Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
        }
        source = { data: new Uint8Array(bytes) };
    }
    else { await response.body?.cancel(); source = { url }; }
    if (signal?.aborted) throw new DOMException("Apertura annullata", "AbortError");
    if (hash && verifiedAlternate) {
        const alternate = await verifiedAlternate(hash);
        if (alternate) {
            const prepared = await preparePdfDocument(alternate, engineReady, { signal, prefetchOnly });
            return prepared ? Object.freeze({ ...prepared, sourceSha256: hash }) : null;
        }
    }
    const prepared = documents.acquire(hash, () => api.getDocument({ ...source, worker, cMapUrl: `${base}cmaps/`, cMapPacked: true,
        standardFontDataUrl: `${base}standard_fonts/`, wasmUrl: `${base}wasm/`, isEvalSupported: false,
    }));
    const abortPreparation = () => { prepared.discard(); prepared.cancel()?.catch(() => {}); };
    signal?.addEventListener("abort", abortPreparation, { once: true });
    try {
        // Speculative preparation warms operations; a real click lets PDF.js
        // stream them while the dialog and first page are being laid out.
        if (prefetchOnly && source.data && !prepared.reused) {
            const document = await prepared.ready;
            const page = await document.getPage(1);
            await Promise.all([page.getOperatorList({ annotationMode: api.AnnotationMode.ENABLE }), readPdfPageRotation(page)]);
        }
    } catch (error) {
        // The normal viewer reports decoding failures and offers the legacy tools.
        if (signal?.aborted) { abortPreparation(); throw error; }
    } finally { signal?.removeEventListener("abort", abortPreparation); }
    if (signal?.aborted) { abortPreparation(); throw new DOMException("Apertura annullata", "AbortError"); }
    return Object.freeze({ ...prepared, sha256: hash });
}

/** Warm first-page operations, optionally publishing the same bounded bitmap. */
export async function warmPdfPage(prepared, signal, onPreview) {
    const task = prepared.take();
    if (!task) return;
    try {
        await renderPdfPreview(await task.promise, signal, onPreview);
    } catch (error) {
        if (!signal.aborted) prepared.discard();
    } finally {
        await task.destroy();
    }
}

/** Render an existing document; the caller retains ownership of its PDF lease. */
export async function renderPdfPreview(pdf, signal, onPreview) {
    let canvas, render;
    const abort = () => render?.cancel();
    try {
        if (signal.aborted || pdf.numPages > 20) return;
        const page = await pdf.getPage(1);
        const rotation = (page.rotate + await readPdfPageRotation(page)) % 360;
        const original = page.getViewport({ scale: 1, rotation });
        const scale = Math.min((window.innerWidth - 24) / original.width, (window.innerHeight - 104) / original.height,
            Math.sqrt(1500000 / (original.width * original.height)), 1);
        const viewport = page.getViewport({ scale, rotation });
        canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const { api } = await loadPdfEngine();
        if (signal.aborted) return;
        render = page.render({ canvasContext: canvas.getContext("2d"), viewport,
            annotationMode: api.AnnotationMode.ENABLE, background: "rgb(255,255,255)",
        });
        signal.addEventListener("abort", abort, { once: true });
        await render.promise;
        if (!signal.aborted && onPreview) onPreview(canvas.toDataURL("image/jpeg", 0.9));
    } finally {
        signal.removeEventListener("abort", abort);
        if (canvas) { canvas.width = 0; canvas.height = 0; }
    }
}
